import { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Radio,
  Paper,
  useTheme,
} from "@mui/material";

import { ButtonV1, ModalBox, Loader } from "components";
import { PAYMENT_CHANGE_HEADERS } from "../constants";
import { formatDateTime_v2, moneyFormat } from "utils";

const PaymentChangeRequestModal = ({
  open,
  onClose,
  data,
  isLoading = false,
  onSubmit,
}) => {
  const theme = useTheme();
  const [selectedOption, setSelectedOption] = useState(null);

  const options = data?.options || [];

  const handleSelect = (option) => {
    if (!option?.eligible) return;
    if (
      selectedOption?.creditLimitId === option?.creditLimitId ||
      selectedOption === option
    ) {
      setSelectedOption(null);
    } else {
      setSelectedOption(option);
    }
  };

  const handleSubmit = async () => {
    if (selectedOption && onSubmit) {
      await onSubmit(selectedOption);
    }
  };

  return (
    <ModalBox open={open} onCloseModal={onClose} width="85%">
      <Box p={2}>
        <Typography variant="h3" fontWeight={700} mb={3}>
          Change payment instrument request
        </Typography>

        <Box
          display="flex"
          alignItems="center"
          gap={3}
          my={3}
          p={2.5}
          sx={{
            borderRadius: 1,
            border: `1px solid ${theme.palette.grey[400]}`,
          }}
        >
          <Typography>
            Order No: <strong>{data?.ordNumber || "-"}</strong>
          </Typography>
          <Typography>
            Current instrument:{" "}
            <strong>{data?.currentCreditProgramName || "-"}</strong>
          </Typography>
          <Typography>
            Pending DO credit:{" "}
            <strong>{moneyFormat(data?.pendingDoCreditAmount ?? 0)}</strong>
          </Typography>
        </Box>

        <TableContainer
          component={Paper}
          sx={{ mt: 2, borderRadius: 2, maxHeight: 450 }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {PAYMENT_CHANGE_HEADERS.map((header, index) => (
                  <TableCell key={index} sx={{ whiteSpace: "nowrap" }}>
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={PAYMENT_CHANGE_HEADERS.length}
                    align="center"
                  >
                    <Loader sx={{ marginTop: 15 }} />
                  </TableCell>
                </TableRow>
              ) : options.length ? (
                options.map((option, index) => {
                  const isEligible = option?.eligible === true;
                  const isSelected =
                    selectedOption?.creditLimitId === option?.creditLimitId;
                  const failureMessage =
                    option?.failures
                      ?.map((message) => message?.message)
                      .join(", ") || "-";

                  return (
                    <TableRow
                      key={option?.creditLimitId || index}
                      hover={isEligible}
                      onClick={() => handleSelect(option)}
                    >
                      <TableCell padding="checkbox">
                        <Radio
                          checked={isSelected}
                          disabled={!isEligible}
                          value={option?.creditLimitId}
                          size="small"
                          sx={{
                            "&.Mui-disabled": {
                              pointerEvents: "auto",
                              cursor: "not-allowed",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>{option?.creditProgramName || "-"}</TableCell>
                      <TableCell>{option?.creditLimitId || "-"}</TableCell>
                      <TableCell>{option?.creditProduct || "-"}</TableCell>
                      <TableCell>{option?.paymentMode || "-"}</TableCell>
                      <TableCell>
                        {moneyFormat(option?.creditUsable || 0)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatDateTime_v2(option?.creditExpiry)}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: isEligible
                            ? theme.palette.success.dark
                            : theme.palette.error.dark,
                          fontWeight: 600,
                        }}
                      >
                        {isEligible ? "Eligible" : "Not eligible"}
                      </TableCell>
                      <TableCell>{isEligible ? "-" : failureMessage}</TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={PAYMENT_CHANGE_HEADERS.length}
                    align="center"
                    sx={{ py: 4 }}
                  >
                    No payment change options available.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
          <ButtonV1 variant="outlined" title="Cancel" onClick={onClose} />
          <ButtonV1
            variant="contained"
            title="Submit request"
            disabled={!selectedOption}
            onClick={handleSubmit}
          />
        </Box>
      </Box>
    </ModalBox>
  );
};

PaymentChangeRequestModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func,
};

export default PaymentChangeRequestModal;
